import { action } from "@ember/object";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import {
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { ValidatorAction, ValidatorMap } from "ember-changeset/types";
import type { IntlService } from "ember-intl";
import ForcJournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import { DfgLoinc, RacLoinc } from "parog-web/models/bio-exam-result";
import type { BufferedChangeset } from "validated-changeset";
import { lookupValidator } from "validated-changeset";

export default abstract class BaseKfreEditionForm extends ForcJournalEventBasedComponent {
  @service declare intl: IntlService;

  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  journalEventProperties = ["age", "dfg", "rac", "occurredAt", "score"];

  private isComputingScore = false;

  @tracked
  selectedTabButton: "first" | "second" = "first";

  @action
  onTabButtonClicked() {
    this.selectedTabButton =
      this.selectedTabButton === "first" ? "second" : "first";
  }

  validator: ValidatorAction = o => {
    return (
      lookupValidator(this.currentValidations) as NonNullable<ValidatorAction>
    )(o);
  };

  get changeset(): BufferedChangeset {
    const changeset = super.changeset;
    changeset.on("afterValidation", () => {
      if (!this.isComputingScore && this.canComputeScore(this.changeset)) {
        this.isComputingScore = true;
        this.changeset.set("score", this.computeScore(this.changeset));
        this.isComputingScore = false;
      }
    });
    return changeset;
  }

  retrieveModel() {
    const model = super.retrieveModel();

    if (this.isNewMode) {
      const dfgResult =
        this.args.patient.getLastBioExamResultWithDate(DfgLoinc);
      const racResult =
        this.args.patient.getLastBioExamResultWithDate(RacLoinc);

      model.occurredAt = new Date();
      model.age = this.patient.age || model.age;
      model.sex = this.patient.gender;
      model.dfg = dfgResult?.result.value;
      model.dfgOccurredAt = dfgResult?.occurredAt;
      model.rac = racResult?.result.value;
      model.racOccurredAt = racResult?.occurredAt;
      model.score = this.canComputeScore(model) ? this.computeScore(model) : "";
    }

    return model;
  }

  get racDateLabel(): string {
    const racDate = this.changeset.get("racOccurredAt");
    return racDate ? `Le ${this.intl.formatDate(racDate)}` : "";
  }

  get dfgDateLabel(): string {
    const dfgDate = this.changeset.get("dfgOccurredAt");
    return dfgDate ? `Le ${this.intl.formatDate(dfgDate)}` : "";
  }

  get isDirty(): boolean {
    return (
      super.isDirty || (this.isNewMode && this.canComputeScore(this.changeset))
    );
  }

  canComputeScore(o): boolean {
    return ["age", "sex", "dfg", "rac"].every(key => o[key]);
  }

  abstract computeScore(o);

  get score(): string {
    return this.canComputeScore(this.changeset)
      ? this.computeScore(this.changeset)
      : "";
  }

  get surveyValidations(): ValidatorMap {
    return {
      age: validatePresence({
        presence: true,
        description: "L'age"
      }),
      sex: validatePresence({
        presence: true,
        description: "Le sexe"
      }),
      dfg: [
        validatePresence({
          presence: true,
          description: "Le dernier DFG"
        }),
        validateNumber({
          positive: true,
          description: "Le dernier DFG"
        })
      ],
      rac: [
        validatePresence({
          presence: true,
          description: "Le dernier RAC"
        }),
        validateNumber({
          positive: true,
          description: "Le dernier RAC"
        })
      ]
    };
  }

  get totalValidations(): ValidatorMap {
    return {
      score: [
        validatePresence({
          presence: true,
          description: "Le total"
        }),
        validateNumber({
          lte: 100,
          positive: true,
          description: "Le total"
        })
      ],
      occurredAt: validatePresence({
        presence: true,
        description: "La date"
      })
    };
  }

  get validations(): ValidatorMap {
    return { ...this.surveyValidations, ...this.totalValidations };
  }

  get currentValidations(): ValidatorMap {
    return this.selectedTabButton == "first"
      ? this.surveyValidations
      : this.totalValidations;
  }
}
